import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { leaveBalancesApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { LeaveBalance } from '@/types/hr';
import { Badge, Days, leaveYearOf } from './shared';
import { EmployeePicker, type PickedEmployee } from './EmployeePicker';

/**
 * What one employee has left, per leave type.
 *
 * <p>Balances are created on first read rather than by a yearly batch, so an
 * employee who joined in March has one without a job having run. HR can adjust
 * an entitlement, but never below what is already taken or pending: somebody has
 * approved those days and the employee may already be away.
 */
export function LeaveBalancesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.LEAVE_BALANCE_MANAGE);

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [year, setYear] = useState(leaveYearOf());
  const [adjusting, setAdjusting] = useState<LeaveBalance | null>(null);
  const [entitled, setEntitled] = useState('');
  const [carried, setCarried] = useState('');
  const [reason, setReason] = useState('');

  const balances = useQuery({
    queryKey: queryKeys.leaveBalances.forEmployee(employee?.id ?? '', year),
    queryFn: () => leaveBalancesApi.forEmployee(employee!.id, year),
    enabled: employee !== null,
  });

  const adjust = useMutation({
    mutationFn: () =>
      leaveBalancesApi.adjust({
        employeeId: employee!.id,
        leaveTypeId: adjusting!.leaveTypeId,
        year,
        entitledDays: entitled === '' ? null : Number(entitled),
        carriedForwardDays: carried === '' ? null : Number(carried),
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    onSuccess: (balance) => {
      toast.success(`${balance.leaveTypeName} adjusted`);
      setAdjusting(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.leaveBalances.all });
    },
    onError: (error) => handleApiError(error),
  });

  const open = (balance: LeaveBalance) => {
    setAdjusting(balance);
    setEntitled(String(balance.entitledDays));
    setCarried(String(balance.carriedForwardDays));
    setReason('');
  };

  const columns: Column<LeaveBalance>[] = [
    {
      key: 'type',
      header: 'Leave type',
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="text-sm text-ink">{row.leaveTypeName}</span>
          {!row.paid && <Badge tone="warning">Unpaid</Badge>}
        </span>
      ),
    },
    {
      key: 'entitled',
      header: 'Entitled',
      className: 'text-right',
      render: (row) => <Days value={row.entitledDays} />,
    },
    {
      key: 'carried',
      header: 'Carried in',
      className: 'text-right',
      render: (row) =>
        row.carriedForwardDays === 0 ? (
          <span className="text-subtle">—</span>
        ) : (
          <Days value={row.carriedForwardDays} />
        ),
    },
    {
      key: 'taken',
      header: 'Taken',
      className: 'text-right',
      render: (row) => <Days value={row.takenDays} />,
    },
    {
      key: 'pending',
      header: 'Pending',
      className: 'text-right',
      render: (row) =>
        row.pendingDays === 0 ? (
          <span className="text-subtle">—</span>
        ) : (
          <span className="text-[color:var(--sms-warning)]">
            <Days value={row.pendingDays} />
          </span>
        ),
    },
    {
      key: 'remaining',
      header: 'Left',
      className: 'text-right',
      render: (row) => (
        <span className="font-bold text-ink">
          <Days value={row.remainingDays} />
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <Button size="sm" variant="secondary" onClick={() => open(row)}>
            Adjust
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leave Balances"
        description="What an employee has left, and where it went."
        icon={<Clock className="h-5 w-5" aria-hidden="true" />}
      />

      <Card className="p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <div className="max-w-sm">
            <EmployeePicker value={employee} onChange={setEmployee} />
          </div>
          <div className="w-32">
            <TextField
              label="Year"
              type="number"
              min="2000"
              max="2200"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
            />
          </div>
        </div>
      </Card>

      {employee === null ? (
        <Card className="mt-3 p-8 text-center">
          <p className="text-sm text-muted">Choose an employee to see their leave balances.</p>
        </Card>
      ) : (
        <Card className="mt-3 overflow-hidden">
          <DataTable
            columns={columns}
            rows={balances.data}
            rowKey={(row) => row.id}
            loading={balances.isPending}
            error={balances.error}
            onRetry={() => void balances.refetch()}
            emptyTitle="No balances"
            emptyDescription="No active leave types are configured."
          />
        </Card>
      )}

      {adjusting !== null && (
        <Modal
          open
          onClose={() => setAdjusting(null)}
          title={`Adjust ${adjusting.leaveTypeName} for ${year}`}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              adjust.mutate();
            }}
          >
            <p className="border border-line bg-surface p-3 text-[13px] text-muted">
              <Days value={adjusting.takenDays} /> taken and{' '}
              <Days value={adjusting.pendingDays} /> pending. The entitlement cannot be set below
              that — somebody has approved those days.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Entitled days"
                type="number"
                step="0.5"
                min="0"
                value={entitled}
                onChange={(event) => setEntitled(event.target.value)}
              />
              <TextField
                label="Carried forward"
                type="number"
                step="0.5"
                min="0"
                value={carried}
                onChange={(event) => setCarried(event.target.value)}
                hint="Only for types that carry forward"
              />
            </div>

            <TextField
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Pro-rated for a mid-year joiner, correction…"
              hint="Recorded in the audit trail — an adjusted entitlement is asked about a year later"
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setAdjusting(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={adjust.isPending}>
                {adjust.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
